import React, { useEffect, useState } from 'react';
import { useTheme } from '../../theme';
import { ContourField } from './ContourField';
import { StatusDot } from '../../components';

const CONTENT = {
  eyebrowText: 'Learning beyond training',
  headline: 'Small decision models that keep learning.',
  subheadline:
    'Sqwish Labs trains small, specialised models for the real-time decisions inside AI products, and keeps improving them on your production data.',
  primaryCtaText: 'Try OneHot',
  primaryCtaHref: 'https://console.sqwish.ai/#playground',
  secondaryCtaText: 'Read the manifesto',
  secondaryCtaHref: '/manifesto',
};

function Eyebrow({ isVisible, invert }: { isVisible: boolean; invert: boolean }) {
  const textColor = invert ? 'text-foreground-eyebrow' : 'text-foreground-eyebrow';

  return (
    <div
      className={`mb-4 sm:mb-6 opacity-0 ${isVisible ? 'animate-fade-up' : ''}`}
      style={{ animationDelay: '0.1s' }}
    >
      <div className="flex items-center gap-2.5 sm:gap-3">
        <StatusDot
          pulse
          className="w-1 h-1 sm:w-1.5 sm:h-1.5"
          colorClass="bg-brand-amber"
        />
        <span className={`text-eyebrow ${textColor}`}>
          {CONTENT.eyebrowText}
        </span>
      </div>
    </div>
  );
}

function CTAs({ isVisible, invert }: { isVisible: boolean; invert: boolean }) {
  const secondaryBase = `text-xs sm:text-sm font-medium uppercase tracking-wide border ${
    invert ? 'border-white/30 text-white/70' : 'border-foreground/30 text-foreground/70'
  } px-4 sm:px-6 py-2.5 sm:py-3 rounded-full ${
    invert 
      ? 'hover:border-white hover:text-white hover:bg-white/10' 
      : 'hover:border-foreground hover:text-foreground hover:bg-foreground/10'
  } hover:shadow-md transition-all duration-300`;

  const primaryBase = `text-xs sm:text-sm font-medium uppercase tracking-wide ${
    invert ? 'bg-white text-[hsl(220,25%,6%)]' : 'bg-foreground text-background'
  } px-4 sm:px-6 py-2.5 sm:py-3 rounded-full ${
    invert ? 'hover:bg-white/70' : 'hover:bg-foreground/70'
  } hover:shadow-lg transition-all duration-300`;

  return (
    <div
      className={`mt-8 sm:mt-10 flex flex-wrap gap-3 sm:gap-4 opacity-0 ${isVisible ? 'animate-fade-up' : ''}`}
      style={{ animationDelay: '0.5s' }}
    >
      <a href={CONTENT.secondaryCtaHref} className={secondaryBase}>
        {CONTENT.secondaryCtaText}
      </a>
      <a href={CONTENT.primaryCtaHref} target="_blank" rel="noopener noreferrer" className={primaryBase}>
        {CONTENT.primaryCtaText}
      </a>
    </div>
  );
}

function HeroArtwork() {
  return (
    <div
      className="home-hero-artwork pointer-events-none overflow-hidden"
      aria-hidden="true"
    >
      <ContourField />
    </div>
  );
}

export function HeroSection() {
  const [isVisible, setIsVisible] = useState(false);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const invert = isDark;
  const textColor = invert ? 'text-white' : 'text-foreground';
  const subTextColor = invert ? 'text-white/70' : 'text-foreground-secondary';
  const bgClassName = invert ? 'bg-[hsl(220,25%,6%)]' : '';

  const mainColumn = (
    <div className="max-w-full md:max-w-xl lg:max-w-4xl xl:max-w-5xl">
      <Eyebrow isVisible={isVisible} invert={invert} />

      <h1
        className={`
          text-hero-title text-balance
          ${textColor}
          opacity-0 ${isVisible ? 'animate-fade-up' : ''}
        `}
        style={{ animationDelay: '0.2s' }}
      >
        {CONTENT.headline}
      </h1>

      <p
        className={`
          mt-5 sm:mt-6 md:mt-8 text-hero-subtitle max-w-full sm:max-w-md md:max-w-xl lg:max-w-3xl
          ${subTextColor}
          opacity-0 ${isVisible ? 'animate-fade-up' : ''}
        `}
        style={{ animationDelay: '0.35s' }}
      >
        {CONTENT.subheadline}
      </p>

      <CTAs isVisible={isVisible} invert={invert} />
    </div>
  );

  return (
    <section
      className={`hero-section home-hero ${bgClassName} overflow-hidden`}
    >
      <div className="home-hero-frame relative z-10 mx-auto w-full hero-section-content">
        <div className="home-hero-copy lg:min-h-[320px]">
          {mainColumn}
        </div>
      </div>

      <HeroArtwork />

      <div
        className={`absolute bottom-0 left-0 right-0 h-px ${
          invert ? 'bg-white/10' : 'bg-border'
        }`}
      />
    </section>
  );
}

export default HeroSection;
